import { useState, useEffect } from "react";
import {
  LayoutDashboard,
  ClipboardCheck,
  PackageOpen,
  CreditCard,
  Activity,
  ChevronRight,
  TrendingUp,
  Kanban,
  ListChecks,
  UserPlus,
  HeartPulse,
  Flag,
  LogOut,
  Loader2,
} from "lucide-react";
import { MOCK_CLIENTS } from "./mockData";
import { MockBanner } from "./ui";
import { useMockAuth } from "./MockAuth";
import { useAuth } from "./auth/AuthContext";
import { ClientsDashboard } from "./ClientsDashboard";
import { CompanyDetail } from "./CompanyDetail";
import { OnboardingChecklist } from "./OnboardingChecklist";
import { CatalogOnboarding } from "./CatalogOnboarding";
import { BillingPlaceholder } from "./BillingPlaceholder";
import { OperatorActivity } from "./OperatorActivity";
import { RevenueDashboard } from "./RevenueDashboard";
import { PipelineKanban } from "./PipelineKanban";
import { FollowUpTasks } from "./FollowUpTasks";
import { CustomerCreationWizard } from "./CustomerCreationWizard";
import { LiveTenantProvisioningWizard } from "./LiveTenantProvisioningWizard";
import { PlatformHealth } from "./PlatformHealth";
import { FeatureFlags } from "./FeatureFlags";
import { PlatformStats } from "./PlatformStats";
import { StoreCoverage } from "./StoreCoverage";
import { isLiveMode, isSurfaceLive, platformGet } from "./api/platformClient";
import { LiveBadge, UnavailablePanel } from "./ui-badges";
import { adaptCompanyDetail, unwrap } from "./api/adapters";
import { BHLMark } from "./BHLLogo";

// Truthful-dashboard policy:
// In live mode we deliberately HIDE every nav item that would render
// fabricated CRM/sales/billing data. Each is documented inline so it is
// obvious why it is missing from the live console.
const NAV_PLATFORM = [
  { key: "health", label: "Platform health", icon: HeartPulse },
  { key: "feature_flags", label: "Feature flags", icon: Flag },
];

const NAV_PRIMARY_MOCK = [
  { key: "revenue", label: "Revenue", icon: TrendingUp, mock: true },
  { key: "pipeline", label: "Pipeline", icon: Kanban, mock: true },
  { key: "tasks", label: "Tasks", icon: ListChecks, mock: true },
];

const NAV_OPS_BASE = [
  { key: "clients", label: "Clients", icon: LayoutDashboard },
  { key: "catalog", label: "Catalog", icon: PackageOpen },
  { key: "activity", label: "Activity", icon: Activity },
];

const NAV_OPS_MOCK_EXTRAS = [
  { key: "onboarding", label: "Onboarding", icon: ClipboardCheck, mock: true },
  { key: "billing", label: "Billing", icon: CreditCard, mock: true },
];

// Build the navigation per-mode. Live mode hides every mock-only surface.
const navForMode = (live) => {
  const platform = NAV_PLATFORM;
  const primary = live ? [] : NAV_PRIMARY_MOCK;
  const ops = live ? NAV_OPS_BASE : [
    NAV_OPS_BASE[0],
    NAV_OPS_MOCK_EXTRAS[0],
    NAV_OPS_BASE[1],
    NAV_OPS_MOCK_EXTRAS[1],
    NAV_OPS_BASE[2],
  ];
  return { platform, primary, ops, flat: [...platform, ...primary, ...ops] };
};

export const OperatorConsoleShell = () => {
  const live = isLiveMode();
  const nav = navForMode(live);
  const [section, setSection] = useState(live ? "clients" : "revenue");
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [wizardOpen, setWizardOpen] = useState(false);
  const mockAuth = useMockAuth();
  const realAuth = useAuth();

  // Pick which auth context is active
  const user = live ? (realAuth.user || mockAuth.user) : mockAuth.user;
  const logout = live ? realAuth.logout : null;

  // For live mode the dashboard provides only company IDs, so we need to
  // fetch the company detail when the user drills into a client card.
  const [liveSelectedClient, setLiveSelectedClient] = useState(null);
  const [liveSelectedLoading, setLiveSelectedLoading] = useState(false);
  useEffect(() => {
    let cancelled = false;
    if (!live || !selectedClientId) {
      setLiveSelectedClient(null);
      setLiveSelectedLoading(false);
      return;
    }
    if (!isSurfaceLive("COMPANY_DETAIL")) return;
    setLiveSelectedLoading(true);
    (async () => {
      try {
        const data = await platformGet(`/api/platform/companies/${selectedClientId}`);
        const companyObj = unwrap(data, "company") || data;
        if (!cancelled) {
          setLiveSelectedClient({
            id: selectedClientId,
            ...adaptCompanyDetail(companyObj || {}),
          });
        }
      } catch {
        // CompanyDetail surfaces its own error UI; keep fallback selectedClient.
      } finally {
        if (!cancelled) setLiveSelectedLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      setLiveSelectedLoading(false);
    };
  }, [live, selectedClientId]);

  const mockSelectedClient = selectedClientId
    ? MOCK_CLIENTS.find((c) => c.id === selectedClientId)
    : null;

  // In LIVE mode we refuse to synthesize zero/"—" placeholders for an
  // unknown tenant — we either show real ShelfLife data, a loading state,
  // or the empty selection. Mock mode keeps the legacy synthetic fallback.
  const selectedClient = live
    ? liveSelectedClient
    : (mockSelectedClient ||
        (selectedClientId
          ? {
              id: selectedClientId,
              name: selectedClientId,
              stores: [],
              departments: [],
              users: [],
              status: "active",
              storeCount: 0,
              userCount: 0,
              catalogItems: 0,
              plan: "—",
              billingStatus: "not_set",
              catalogStatus: "not_started",
              headquarters: "—",
              createdAt: "—",
              lastActivity: "—",
              accountOwner: "—",
              notes: "",
            }
          : null));

  // In live mode the catalog client picker is only meaningful once we have
  // a live tenant in focus; we do NOT show the synthetic MOCK_CLIENTS list
  // for tenant selection. In mock mode the existing behaviour is preserved.
  const catalogClients = live ? [] : MOCK_CLIENTS;
  const focusClient = selectedClient || (!live && section === "catalog" ? MOCK_CLIENTS[0] : null);

  // In live mode, the customer-creation wizard is mock-only UNLESS the
  // tenant-provisioning feature flag is enabled (Phase 3B.2).
  const tenantProvisioningLive =
    live && process.env.REACT_APP_LIVE_TENANT_PROVISIONING === "true";
  const showNewCustomerButton = !live || tenantProvisioningLive;

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900" data-testid="operator-console-root">
      {!live && <MockBanner />}
      <div className="flex">
        <Sidebar
          nav={nav}
          section={section}
          onSection={(k) => {
            setSection(k);
            if (k === "clients") setSelectedClientId(null);
          }}
          onNewCustomer={showNewCustomerButton ? () => setWizardOpen(true) : null}
          user={user}
          live={live}
          onLogout={logout}
        />
        <main className="flex-1 min-h-[calc(100vh-32px)]">
          <TopBar
            nav={nav}
            section={section}
            client={selectedClient}
            onClearClient={() => setSelectedClientId(null)}
            onNewCustomer={showNewCustomerButton ? () => setWizardOpen(true) : null}
          />
          <div className="max-w-[1400px] mx-auto px-8 py-8">
            {section === "health" && <PlatformHealth />}
            {section === "feature_flags" && <FeatureFlags />}

            {/* Mock-only sections — never reachable from the live nav, but
                if a deep-link sets `section` we still render an explicit
                unavailable panel rather than fabricated numbers. */}
            {section === "revenue" && (live ? (
              <UnavailablePanel
                title="Revenue dashboard not available"
                reason="Revenue and ARR metrics are not exposed by the read-only ShelfLife platform API in Phase 2C. This surface will return when billing data becomes available (Phase 3+)."
                testId="unavailable-revenue"
              />
            ) : <RevenueDashboard />)}
            {section === "pipeline" && (live ? (
              <UnavailablePanel
                title="Prospect pipeline not available"
                reason="Sales pipeline data is not part of the ShelfLife platform API. It belonged to the mock CRM prototype and has been hidden in live mode."
                testId="unavailable-pipeline"
              />
            ) : <PipelineKanban />)}
            {section === "tasks" && (live ? (
              <UnavailablePanel
                title="Follow-up tasks not available"
                reason="Follow-up tasks are not exposed by ShelfLife. This surface was part of the mock CRM prototype and has been hidden in live mode."
                testId="unavailable-tasks"
              />
            ) : <FollowUpTasks />)}

            {section === "clients" && !selectedClient && (
              <ClientsDashboard
                clients={live ? [] : MOCK_CLIENTS}
                onSelect={(id) => setSelectedClientId(id)}
              />
            )}

            {section === "clients" && live && selectedClientId && !selectedClient && (
              <div className="rounded-xl border border-slate-200 bg-white p-8 text-center">
                {liveSelectedLoading ? (
                  <>
                    <Loader2 className="mx-auto h-5 w-5 text-slate-400 animate-spin" />
                    <p className="mt-2 text-sm text-slate-500">
                      Loading tenant from ShelfLife…
                    </p>
                  </>
                ) : (
                  <p className="text-sm text-slate-500">
                    ShelfLife did not return detail for this tenant.
                  </p>
                )}
              </div>
            )}

            {section === "clients" && selectedClient && (
              <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
                <div className="xl:col-span-2">
                  <CompanyDetail
                    client={selectedClient}
                    onBack={() => setSelectedClientId(null)}
                  />
                </div>
                <div className="space-y-4">
                  <PlatformStats companyId={selectedClient.id} />
                  <StoreCoverage companyId={selectedClient.id} />
                  {/* In live mode the onboarding checklist and billing
                      placeholder are mock-only — hide them entirely on
                      the company sidebar so operators are not misled. */}
                  {!live && (
                    <>
                      <OnboardingChecklist
                        clientId={selectedClient.id}
                        clientName={selectedClient.name}
                      />
                      <BillingPlaceholder
                        clientId={selectedClient.id}
                        clientName={selectedClient.name}
                      />
                    </>
                  )}
                </div>
              </div>
            )}

            {section === "onboarding" && (live ? (
              <UnavailablePanel
                title="Onboarding checklist not available"
                reason="Onboarding progress is not currently derived from live ShelfLife state. The previous checklist was a hardcoded mock and has been removed. Onboarding tracking will return once Phase 3 provisioning is in place."
                testId="unavailable-onboarding"
              />
            ) : (focusClient && (
              <div className="space-y-6">
                <ClientPicker
                  clients={catalogClients}
                  selectedId={focusClient.id}
                  onSelect={(id) => setSelectedClientId(id)}
                />
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <OnboardingSummary client={focusClient} />
                  <OnboardingChecklist
                    clientId={focusClient.id}
                    clientName={focusClient.name}
                  />
                </div>
              </div>
            )))}

            {section === "catalog" && (
              live ? (
                selectedClient ? (
                  <CatalogOnboarding
                    clientId={selectedClient.id}
                    clientName={selectedClient.name}
                  />
                ) : (
                  <UnavailablePanel
                    title="Pick a tenant to view catalog status"
                    reason="Choose a client from the Clients section to view their ShelfLife catalog import status. Catalog data is read-only and tenant-scoped."
                    testId="catalog-no-tenant"
                  />
                )
              ) : (focusClient && (
                <div className="space-y-6">
                  <ClientPicker
                    clients={catalogClients}
                    selectedId={focusClient.id}
                    onSelect={(id) => setSelectedClientId(id)}
                  />
                  <CatalogOnboarding
                    clientId={focusClient.id}
                    clientName={focusClient.name}
                  />
                </div>
              ))
            )}

            {section === "billing" && (live ? (
              <UnavailablePanel
                title="Billing not available"
                reason="Billing data is not exposed by the ShelfLife platform API in Phase 2C. Stripe is deferred to Phase 3+."
                testId="unavailable-billing"
              />
            ) : (focusClient && (
              <div className="space-y-6">
                <ClientPicker
                  clients={catalogClients}
                  selectedId={focusClient.id}
                  onSelect={(id) => setSelectedClientId(id)}
                />
                <BillingPlaceholder
                  clientId={focusClient.id}
                  clientName={focusClient.name}
                />
              </div>
            )))}

            {section === "activity" && (
              <OperatorActivity companyId={selectedClientId || undefined} />
            )}
          </div>
        </main>
      </div>
      {wizardOpen && !live && (
        <CustomerCreationWizard onClose={() => setWizardOpen(false)} />
      )}
      {wizardOpen && tenantProvisioningLive && (
        <LiveTenantProvisioningWizard
          onClose={() => setWizardOpen(false)}
          onTenantCreated={(cid) => { setSelectedClientId(cid); setSection("clients"); setWizardOpen(false); }}
        />
      )}
    </div>
  );
};

const Sidebar = ({ nav, section, onSection, onNewCustomer, user, live, onLogout }) => (
  <aside
    data-testid="operator-console-sidebar"
    className="w-64 shrink-0 bg-[#0d3b66] text-slate-100 min-h-screen flex flex-col"
  >
    <div className="px-5 py-5 border-b border-white/10">
      <div className="flex items-center gap-2.5">
        <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-white/10 ring-1 ring-white/20">
          <BHLMark tone="light" size={22} />
        </span>
        <div>
          <div className="text-[11px] uppercase tracking-widest text-white/60">
            Blue Harbor Labs
          </div>
          <div className="text-sm font-semibold tracking-tight">
            Control Center
          </div>
        </div>
      </div>
    </div>

    {onNewCustomer && (
      <div className="px-3 pt-4">
        <button
          type="button"
          data-testid="sidebar-new-customer-btn"
          onClick={onNewCustomer}
          className="w-full inline-flex items-center justify-center gap-1.5 rounded-lg bg-[#f4d35e] text-[#0d3b66] px-3 py-2 text-sm font-semibold hover:bg-[#fce58a] transition-colors"
        >
          <UserPlus className="h-4 w-4" /> New customer
        </button>
      </div>
    )}

    <nav className="flex-1 px-3 py-4 space-y-1">
      <SidebarSectionLabel>Platform</SidebarSectionLabel>
      {nav.platform.map((n) => (
        <SidebarItem key={n.key} item={n} active={n.key === section} onSection={onSection} />
      ))}
      {nav.primary.length > 0 && (
        <>
          <div className="h-2" />
          <SidebarSectionLabel>Growth</SidebarSectionLabel>
          {nav.primary.map((n) => (
            <SidebarItem key={n.key} item={n} active={n.key === section} onSection={onSection} />
          ))}
        </>
      )}
      <div className="h-2" />
      <SidebarSectionLabel>Operations</SidebarSectionLabel>
      {nav.ops.map((n) => (
        <SidebarItem key={n.key} item={n} active={n.key === section} onSection={onSection} />
      ))}
    </nav>

    <div className="px-3 py-4 border-t border-white/10">
      <div className="rounded-lg bg-white/5 px-3 py-2.5">
        <div className="text-[11px] uppercase tracking-wide text-white/50">
          Signed in
        </div>
        <div className="text-sm font-medium text-white mt-0.5">{user?.name || user?.email || "—"}</div>
        <div className="text-[11px] text-white/60">{user?.email}</div>
        <div className="mt-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-[#f4d35e] font-semibold">
          <span className="h-1.5 w-1.5 rounded-full bg-[#f4d35e]" />
          {live ? "platform_superadmin" : "Admin (mock)"}
        </div>
        {live && onLogout && (
          <button
            type="button"
            data-testid="sidebar-logout-btn"
            onClick={onLogout}
            className="mt-3 w-full inline-flex items-center justify-center gap-1 rounded-md bg-white/10 hover:bg-white/15 text-xs font-medium text-white px-2 py-1.5 transition-colors"
          >
            <LogOut className="h-3.5 w-3.5" /> Sign out
          </button>
        )}
      </div>
    </div>
  </aside>
);

const SidebarSectionLabel = ({ children }) => (
  <div className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-widest text-white/40 font-semibold">
    {children}
  </div>
);

const SidebarItem = ({ item, active, onSection }) => {
  const Icon = item.icon;
  return (
    <button
      type="button"
      data-testid={`nav-${item.key}`}
      onClick={() => onSection(item.key)}
      className={`w-full flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
        active
          ? "bg-white/10 text-white"
          : "text-slate-300 hover:bg-white/5 hover:text-white"
      }`}
    >
      <Icon className="h-4 w-4" />
      <span className="flex-1 text-left">{item.label}</span>
      {item.mock && (
        <span className="text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-400/15 text-amber-200 ring-1 ring-amber-400/30">
          Mock
        </span>
      )}
    </button>
  );
};

const TopBar = ({ nav, section, client, onClearClient, onNewCustomer }) => {
  const sectionLabel = nav.flat.find((n) => n.key === section)?.label || "";
  return (
    <div className="border-b border-slate-200 bg-white">
      <div className="max-w-[1400px] mx-auto px-8 py-3 flex items-center justify-between gap-3 text-sm">
        <div className="flex items-center gap-2">
          <span className="text-slate-500">Control Center</span>
          <ChevronRight className="h-3.5 w-3.5 text-slate-400" />
          <button
            type="button"
            onClick={onClearClient}
            className="text-slate-700 hover:text-[#0d3b66] font-medium"
            data-testid="breadcrumb-section"
          >
            {sectionLabel}
          </button>
          {client && (
            <>
              <ChevronRight className="h-3.5 w-3.5 text-slate-400" />
              <span className="text-slate-900 font-semibold">{client.name}</span>
            </>
          )}
        </div>
        {onNewCustomer && (
          <button
            type="button"
            data-testid="topbar-new-customer-btn"
            onClick={onNewCustomer}
            className="inline-flex items-center gap-1.5 rounded-lg bg-[#0d3b66] text-white px-3 py-1.5 text-xs font-semibold hover:bg-[#0a2e52] transition-colors"
          >
            <UserPlus className="h-3.5 w-3.5" /> New customer
          </button>
        )}
      </div>
    </div>
  );
};

const ClientPicker = ({ clients, selectedId, onSelect }) => (
  <div
    data-testid="client-picker"
    className="rounded-xl border border-slate-200 bg-white p-3 flex items-center gap-2 flex-wrap"
  >
    <span className="text-xs uppercase tracking-wide text-slate-500 px-2">
      Client
    </span>
    {clients.map((c) => (
      <button
        type="button"
        key={c.id}
        data-testid={`client-picker-${c.id}`}
        onClick={() => onSelect(c.id)}
        className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
          c.id === selectedId
            ? "bg-[#0d3b66] text-white"
            : "bg-slate-100 text-slate-700 hover:bg-slate-200"
        }`}
      >
        {c.name}
      </button>
    ))}
  </div>
);

const OnboardingSummary = ({ client }) => (
  <div
    data-testid={`onboarding-summary-${client.id}`}
    className="rounded-xl border border-slate-200 bg-white p-5"
  >
    <div className="text-xs uppercase tracking-wide text-slate-500">
      Onboarding focus
    </div>
    <h2 className="mt-1 text-xl font-semibold text-slate-900">{client.name}</h2>
    <p className="mt-2 text-sm text-slate-600 leading-relaxed">{client.notes}</p>

    <div className="mt-5 grid grid-cols-2 gap-3 text-sm">
      <div className="rounded-lg bg-slate-50 p-3">
        <div className="text-[11px] uppercase tracking-wide text-slate-500">Account owner</div>
        <div className="mt-0.5 font-medium text-slate-900">{client.accountOwner}</div>
      </div>
      <div className="rounded-lg bg-slate-50 p-3">
        <div className="text-[11px] uppercase tracking-wide text-slate-500">Headquarters</div>
        <div className="mt-0.5 font-medium text-slate-900">{client.headquarters}</div>
      </div>
      <div className="rounded-lg bg-slate-50 p-3">
        <div className="text-[11px] uppercase tracking-wide text-slate-500">Stores</div>
        <div className="mt-0.5 font-medium text-slate-900">{client.storeCount}</div>
      </div>
      <div className="rounded-lg bg-slate-50 p-3">
        <div className="text-[11px] uppercase tracking-wide text-slate-500">Users</div>
        <div className="mt-0.5 font-medium text-slate-900">{client.userCount}</div>
      </div>
      <div className="rounded-lg bg-slate-50 p-3">
        <div className="text-[11px] uppercase tracking-wide text-slate-500">Plan</div>
        <div className="mt-0.5 font-medium text-slate-900">{client.plan}</div>
      </div>
      <div className="rounded-lg bg-slate-50 p-3">
        <div className="text-[11px] uppercase tracking-wide text-slate-500">Created</div>
        <div className="mt-0.5 font-medium text-slate-900">{client.createdAt}</div>
      </div>
    </div>

    <div className="mt-5 pt-4 border-t border-slate-100">
      <div className="text-[11px] uppercase tracking-wide text-slate-500">
        Recent activity
      </div>
      <p className="mt-1 text-sm text-slate-700">{client.lastActivity}</p>
    </div>
  </div>
);
